import * as THREE from "three";
import { setTerrainHeightProvider } from "./terrain";
import { paintFarFieldVertexColors } from "./terrain-materials";
import { addHorizonRing } from "./horizon";
import { IRON_MARKET_ROUTE } from "./nav";
import { addIronMarketCity, distanceToStreetGrid } from "./map03-city";

// IRON MARKET (map03) — Theater 3 in THEME.md: a Middle-East city at golden
// hour. Dense flat-roofed blocks, a market corridor, a plaza, rooftop AA,
// oil smoke on the skyline. Urban maps live and die on the massing, so the
// terrain stays a dusty plain and the city carries the read.
// Stylized-only: this map was born after the direction locked.

const ROUTE = IRON_MARKET_ROUTE.map((n) => n.position);

function smoothNoise(x: number, z: number, scale: number): number {
  return (
    Math.sin(x * scale) * 0.42 +
    Math.cos(z * scale * 1.17) * 0.36 +
    Math.sin((x + z) * scale * 0.63) * 0.22
  ) * 0.5 + 0.5;
}

function distanceToSegment(px: number, pz: number, ax: number, az: number, bx: number, bz: number): number {
  const abx = bx - ax;
  const abz = bz - az;
  const lenSq = abx * abx + abz * abz;
  const t = lenSq <= 1e-6 ? 0 : THREE.MathUtils.clamp(((px - ax) * abx + (pz - az) * abz) / lenSq, 0, 1);
  return Math.hypot(px - (ax + abx * t), pz - (az + abz * t));
}

export function map03RouteDistanceAt(x: number, z: number): number {
  let best = Infinity;
  for (let i = 0; i < ROUTE.length - 1; i++) {
    const a = ROUTE[i]!;
    const b = ROUTE[i + 1]!;
    best = Math.min(best, distanceToSegment(x, z, a.x, a.z, b.x, b.z));
  }
  return best;
}

// A river plain: nearly flat with a slow rise away from the city core and
// gentle undulation the blocks can sit on. Edge fades under the far-field.
export function map03HeightAt(x: number, z: number): number {
  const centerDist = Math.hypot(x, z);
  let height = 1.2
    + (smoothNoise(x + 60, z - 40, 0.006) - 0.5) * 5.5
    + THREE.MathUtils.smoothstep(centerDist, 500, 1000) * 14; // rim rise
  // The built-up core is graded almost flat — streets need it
  const cityMask = 1 - THREE.MathUtils.smoothstep(centerDist, 80, 560);
  height = THREE.MathUtils.lerp(height, 1.0 + (smoothNoise(x, z, 0.01) - 0.5) * 1.6, cityMask);
  const edgeDist = Math.max(Math.abs(x), Math.abs(z));
  const edgeFade = 1 - THREE.MathUtils.smoothstep(edgeDist, 960, 1090);
  return THREE.MathUtils.lerp(-2, height, edgeFade);
}

// Palette: sun-bleached dust, asphalt streets in the core, soot near the
// smoke columns. One biome, one palette (WORLD_PRINCIPLES §5).
const DUST = new THREE.Color(0xa8916c);
const DUST_PALE = new THREE.Color(0xb8a37c);
const ASPHALT = new THREE.Color(0x4c483f);
const SOOT = new THREE.Color(0x3b362f);
const RUBBLE_TINT = new THREE.Color(0x83755e);

export const SMOKE_COLUMNS: Array<[number, number]> = [
  [-520, 240],
  [430, -360],
  [120, 520],
  [-360, 480],
];

function paintMap03Ground(geo: THREE.PlaneGeometry): void {
  const pos = geo.attributes["position"] as THREE.BufferAttribute;
  const colors = new Float32Array(pos.count * 3);
  const color = new THREE.Color();

  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    const z = pos.getZ(i);
    const grain = smoothNoise(x, z, 0.03);

    color.copy(DUST).lerp(DUST_PALE, grain * 0.7);

    // Street grid reads as painted asphalt aprons (soft edges are fine in a
    // dusty city; the route street gets a crisp ribbon on top)
    const streetDist = distanceToStreetGrid(x, z);
    const street = 1 - THREE.MathUtils.smoothstep(streetDist, 5, 9);
    color.lerp(ASPHALT, street * 0.8);

    // Soot fans under the smoke columns
    for (const [sx, sz] of SMOKE_COLUMNS) {
      const d = Math.hypot(x - sx, z - sz);
      const soot = 1 - THREE.MathUtils.smoothstep(d, 18, 90);
      if (soot > 0) color.lerp(SOOT, soot * 0.6);
    }

    // Broken ground near (but off) the route corridor — the war came through
    const routeDist = map03RouteDistanceAt(x, z);
    if (routeDist > 40 && routeDist < 160 && smoothNoise(x - 200, z + 90, 0.015) > 0.72) {
      color.lerp(RUBBLE_TINT, 0.5);
    }

    colors[i * 3] = color.r;
    colors[i * 3 + 1] = color.g;
    colors[i * 3 + 2] = color.b;
  }
  geo.setAttribute("color", new THREE.BufferAttribute(colors, 3));
}

const GROUND_MATERIAL = new THREE.MeshStandardMaterial({
  vertexColors: true,
  flatShading: true,
  roughness: 1.0,
  metalness: 0,
});

const ROAD_MATERIAL = new THREE.MeshStandardMaterial({
  color: 0x4e4a42,
  roughness: 0.98,
  metalness: 0,
  polygonOffset: true,
  polygonOffsetFactor: -1,
  polygonOffsetUnits: -2,
});

function resamplePolyline(points: THREE.Vector3[], step: number): THREE.Vector3[] {
  const out: THREE.Vector3[] = [];
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i]!;
    const b = points[i + 1]!;
    const segments = Math.max(1, Math.ceil(a.distanceTo(b) / step));
    for (let s = 0; s < segments; s++) out.push(a.clone().lerp(b, s / segments));
  }
  out.push(points[points.length - 1]!.clone());
  return out;
}

// Upward-wound ground ribbon (the hard-learned winding: base, base+2, base+1)
function makeRoadRibbon(name: string, points: THREE.Vector3[], width: number): THREE.Mesh {
  const positions: number[] = [];
  const indices: number[] = [];
  for (let i = 0; i < points.length; i++) {
    const prev = points[Math.max(0, i - 1)]!;
    const next = points[Math.min(points.length - 1, i + 1)]!;
    const forward = new THREE.Vector3(next.x - prev.x, 0, next.z - prev.z).normalize();
    const right = new THREE.Vector3(forward.z, 0, -forward.x);
    const p = points[i]!;
    const lx = p.x - right.x * width * 0.5;
    const lz = p.z - right.z * width * 0.5;
    const rx = p.x + right.x * width * 0.5;
    const rz = p.z + right.z * width * 0.5;
    positions.push(lx, map03HeightAt(lx, lz) + 0.18, lz);
    positions.push(rx, map03HeightAt(rx, rz) + 0.18, rz);
    if (i < points.length - 1) {
      const base = i * 2;
      indices.push(base, base + 2, base + 1, base + 1, base + 2, base + 3);
    }
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  geo.setIndex(indices);
  geo.computeVertexNormals();
  const road = new THREE.Mesh(geo, ROAD_MATERIAL);
  road.name = name;
  road.receiveShadow = true;
  road.userData["losTransparent"] = true;
  return road;
}

// Oil smoke: tall, tilted, semi-transparent stacked cones — pure skyline
// dressing that sells "a city at war" from orbit height.
function addSmokeColumns(parent: THREE.Group): void {
  const mat = new THREE.MeshBasicMaterial({
    color: 0x2b2823,
    transparent: true,
    opacity: 0.28,
    depthWrite: false,
    fog: true,
  });
  for (const [x, z] of SMOKE_COLUMNS) {
    const column = new THREE.Group();
    const y0 = map03HeightAt(x, z);
    let radius = 9;
    let y = y0;
    for (let seg = 0; seg < 4; seg++) {
      const h = 55 + seg * 12;
      const cone = new THREE.Mesh(new THREE.CylinderGeometry(radius * 1.5, radius, h, 7), mat);
      cone.position.set(x + seg * 9, y + h / 2, z + seg * 4); // wind shear tilt
      column.add(cone);
      y += h * 0.92;
      radius *= 1.45;
    }
    column.traverse((o) => {
      o.userData["losTransparent"] = true;
      o.userData["ignoreBallistics"] = true;
      o.userData["isVFX"] = true;
    });
    parent.add(column);
  }
}

export class Map03Terrain {
  readonly group = new THREE.Group();

  constructor(size = 2200) {
    setTerrainHeightProvider(map03HeightAt);
    const geo = new THREE.PlaneGeometry(size, size, 260, 260);
    geo.rotateX(-Math.PI / 2);
    const pos = geo.attributes["position"] as THREE.BufferAttribute;
    for (let i = 0; i < pos.count; i++) {
      pos.setY(i, map03HeightAt(pos.getX(i), pos.getZ(i)));
    }
    pos.needsUpdate = true;
    geo.computeVertexNormals();
    paintMap03Ground(geo);
    const ground = new THREE.Mesh(geo, GROUND_MATERIAL);
    ground.name = "map03-ground";
    ground.receiveShadow = true;
    this.group.add(ground);

    this.group.add(makeRoadRibbon("map03-route-street", resamplePolyline(IRON_MARKET_ROUTE.map((n) => n.position), 12), 16));

    addIronMarketCity(this.group, map03HeightAt, (x, z) => map03RouteDistanceAt(x, z));
    addSmokeColumns(this.group);
    this.addFarField(size);
    // Dust-choked horizon, warmer and lower than the valley's
    addHorizonRing(this.group, size, { ridge: 0x6a5f50, haze: 0xcbb694 }, 0x1a03c17);
  }

  private addFarField(size: number): void {
    const geo = new THREE.CircleGeometry(size * 1.7, 96);
    geo.rotateX(-Math.PI / 2);
    paintFarFieldVertexColors(geo, size * 0.5, { inner: 0xb9a075, outer: 0xc4ad82, dry: 0xa08a63 });
    const disc = new THREE.Mesh(
      geo,
      new THREE.MeshStandardMaterial({ vertexColors: true, flatShading: true, roughness: 1, metalness: 0 }),
    );
    disc.name = "map03-farfield";
    disc.position.y = -2;
    disc.receiveShadow = true;
    disc.userData["losTransparent"] = true;
    this.group.add(disc);
  }
}
